import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Home,
  Database,
  Edit3,
  Code,
  Book,
  BookOpen,
  FileText,
  MessageSquare,
  Menu,
  X,
  User,
  Users,
  Eye
} from 'react-feather';
import { useAuth } from '../contexts/AuthContext';
import ImpersonationModal from './ImpersonationModal';

interface SidebarItem {
  id: string;
  label: string;
  path: string;
  icon: React.ComponentType<{ size?: number; className?: string }>;
}

const baseSidebarItems: SidebarItem[] = [
  { id: 'dashboard', label: 'Dashboard', path: '/admin', icon: Home },
  { id: 'content-sources', label: 'Content Sources', path: '/admin/content-sources', icon: Database },
  { id: 'domain-content', label: 'Domain Content', path: '/admin/domain-content', icon: BookOpen },
  { id: 'prompts', label: 'Prompt Management', path: '/admin/prompts', icon: Edit3 },
  { id: 'widgets', label: 'Widget Integrations', path: '/admin/widgets', icon: Code },
  { id: 'teaching', label: 'Teaching System', path: '/admin/teaching', icon: Book },
  { id: 'user-management', label: 'User Management', path: '/admin/users', icon: Users },
  { id: 'logs', label: 'System Logs', path: '/admin/logs', icon: FileText },
];

interface AdminLayoutProps {
  children: React.ReactNode;
}

const AdminLayout: React.FC<AdminLayoutProps> = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [impersonationModalOpen, setImpersonationModalOpen] = useState(false);
  const location = useLocation();
  const { user, isAuthenticated } = useAuth();
  
  // Filter sidebar items based on user role
  const sidebarItems = baseSidebarItems.filter(item => {
    // Only show Content Sources to users who can manage content sources (Owners)
    if (item.id === 'content-sources') {
      return user?.capabilities?.canManageContentSources === true;
    }
    // Only show User Management to users who can manage users (Owners)
    if (item.id === 'user-management') {
      return user?.capabilities?.canManageUsers === true;
    }
    // Only show Teaching System to users who can use teaching (Admins and Owners)
    if (item.id === 'teaching') {
      return user?.capabilities?.canUseTeaching === true;
    }
    return true;
  });

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div className={`fixed inset-y-0 left-0 z-50 w-64 bg-eddy-primary transform transition-transform duration-300 ease-in-out lg:translate-x-0 ${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      }`}>
        {/* Header */}
        <div className="bg-eddy-blue text-white p-6 border-b border-white border-opacity-10">
          <div className="flex items-center justify-between">
            <h1 className="text-xl font-semibold">Eddy Admin</h1>
            <button
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden text-white hover:text-gray-300"
            >
              <X size={24} />
            </button>
          </div>
        </div>

        {/* Navigation */}
        <nav className="p-4">
          {/* Back to Chat Link */}
          <Link 
            to="/chat" 
            className="flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-colors duration-200 text-white hover:bg-eddy-blue hover:bg-opacity-50 mb-4 border-b border-white border-opacity-10 pb-4"
          >
            <MessageSquare size={16} className="mr-3" />
            Back to Chat
          </Link>
          
          <ul className="space-y-2">
            {sidebarItems.map((item) => {
              const isActive = location.pathname === item.path || 
                (item.path === '/admin' && location.pathname === '/admin/dashboard');
              
              return (
                <li key={item.id}>
                  <Link
                    to={item.path}
                    onClick={() => setSidebarOpen(false)}
                    className={`flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-colors duration-200 ${
                      isActive
                        ? 'bg-eddy-blue text-white'
                        : 'text-white hover:bg-eddy-blue hover:bg-opacity-50'
                    }`}
                  >
                    <item.icon size={16} className="mr-3" />
                    {item.label}
                  </Link>
                </li>
              );
            })}
          </ul>
        </nav>


        {/* Footer */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-white border-opacity-10">
          <div className="text-sm text-white text-opacity-70">
            <p>Eddy v1.0.0</p>
            <p>© 2025 Treeline</p>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Top header */}
        <header className="bg-white shadow-sm border-b border-gray-200 px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <button
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden mr-3 p-2 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-eddy-blue"
              >
                <Menu size={20} />
              </button>
              <h1 className="text-2xl font-semibold text-gray-900">
                {sidebarItems.find(item => 
                  item.path === location.pathname || 
                  (item.path === '/admin' && location.pathname === '/admin/dashboard')
                )?.label || 'Admin'}
              </h1>
            </div>
            
            <div className="flex items-center space-x-4">
              {/* System Status */}
              <div className="hidden sm:flex items-center space-x-2">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                <span className="text-sm text-gray-600">System Online</span>
              </div>
              
              {/* Impersonation button */}
              {user?.impersonation?.canImpersonate && (
                <button
                  onClick={() => setImpersonationModalOpen(true)}
                  className={`flex items-center space-x-2 px-3 py-2 rounded-lg border transition-colors ${
                    user.impersonation.isImpersonating
                      ? 'bg-purple-100 text-purple-700 border-purple-300 hover:bg-purple-200'
                      : 'bg-gray-100 text-gray-700 border-gray-300 hover:bg-gray-200'
                  }`}
                  title="Role Impersonation"
                >
                  <Eye size={16} />
                  <span className="text-sm font-medium">
                    {user.impersonation.isImpersonating 
                      ? `As ${user.impersonation.effectiveRole === 'admin' ? 'Admin' : 'User'}`
                      : 'Impersonate'
                    }
                  </span>
                  {user.impersonation.isImpersonating && (
                    <div className="w-2 h-2 bg-purple-500 rounded-full animate-pulse"></div>
                  )}
                </button>
              )}
              
              {/* Admin user indicator */}
              {isAuthenticated && user && (
                <div className="flex items-center space-x-2 px-3 py-1 bg-gray-100 rounded-lg">
                  <User size={14} className="text-gray-500" />
                  <span className="text-sm text-gray-700">{user.name}</span>
                  {user.orgId && (
                    <span className="text-xs text-gray-500">({user.orgId})</span>
                  )}
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="p-6">
          {children}
        </main>
      </div>

      {/* Impersonation Modal */}
      <ImpersonationModal 
        isOpen={impersonationModalOpen}
        onClose={() => setImpersonationModalOpen(false)}
      />
    </div>
  );
};

export default AdminLayout;